import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@fluid-app/ui";

const buttonVariants = cva(
  "fluid inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-none bg-transparent p-0 text-sm font-medium transition-colors transition-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "text-foreground hover:bg-transparent hover:text-foreground/90",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-7 w-7 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * If true, the button will show a loading spinner and be disabled
   */
  isLoading?: boolean;
  /**
   * Custom loading spinner element. If not provided, a default spinner will be used
   */
  loadingIndicator?: React.ReactNode;
}

// Define a type for the onClick handler to handle both sync and async functions
type ClickHandler = (
  event: React.MouseEvent<HTMLButtonElement>,
) => void | Promise<void>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading: controlledLoading,
      loadingIndicator,
      onClick,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const [isLoading, setIsLoading] = React.useState(false);
    const isLoadingState = controlledLoading ?? isLoading;

    // Handle async onClick events
    const handleClick = React.useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!onClick || isLoadingState) return;

        try {
          setIsLoading(true);
          // Cast onClick to our ClickHandler type
          const clickHandler = onClick as ClickHandler;
          const result = clickHandler(event);

          // Check if result is a Promise
          if (result instanceof Promise) {
            await result;
          }
        } finally {
          setIsLoading(false);
        }
      },
      [onClick, isLoadingState],
    );

    // Default loading spinner
    const defaultLoadingIndicator = (
      <svg
        className="-ml-1 mr-2 h-4 w-4 animate-spin text-current"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    );

    // When using asChild, we need to handle loading state differently
    // as Slot component expects a single child
    if (asChild && React.isValidElement(children)) {
      const childProps = children.props as { children?: React.ReactNode };

      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          onClick={controlledLoading !== undefined ? onClick : handleClick}
          disabled={disabled ?? isLoadingState}
          {...props}
        >
          {React.cloneElement(
            children,
            {},
            isLoadingState ? (
              <>
                {loadingIndicator ?? defaultLoadingIndicator}
                {childProps.children}
              </>
            ) : (
              childProps.children
            ),
          )}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={controlledLoading !== undefined ? onClick : handleClick}
        disabled={disabled ?? isLoadingState}
        {...props}
      >
        {isLoadingState && (loadingIndicator ?? defaultLoadingIndicator)}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
